/**
 * Manifest — copilote d'authoring admin.
 *
 * @remarks
 * Une feature sans route et sans vue centrale : uniquement un onglet de
 * panneau, présent sur les contextes que le registry de portées couvre. C'est
 * ce qui permet au copilote de suivre l'admin de page en page sans qu'aucune
 * vue existante n'ait à le connaître.
 *
 * **Pas de `panelRequirements` volontairement** : le premier manifest qui en
 * déclare pour un contexte gagne, et le formulaire de nœud en pose déjà (son
 * onglet « Aperçu »). Ouvrir le panneau de force ici le lui volerait. Le
 * copilote s'ouvre à la demande — bouton ✨ d'un champ, onglet, ou ⌘I.
 *
 * Derrière le flag `copilot` tant que le back n'existe pas : la feature est
 * fusionnable sans être montrable.
 */

import { Bot } from 'lucide-react';
import { lazyView, type Context, type FeatureManifest, type TabConfig } from '@/app/registry';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { COPILOT_FEATURE_FLAG, COPILOT_TAB } from './copilotPanels';
import { COPILOT_CONTEXTS } from './scope-registry';

const copilotTab: TabConfig = {
  id: COPILOT_TAB,
  label: 'Copilote',
  icon: Bot,
  defaultSide: 'right',
  view: lazyView(() => import('../views/AdminCopilotPanelView'), 'AdminCopilotPanelView'),
};

const tabsByContext = COPILOT_CONTEXTS.reduce<Partial<Record<Context, readonly TabConfig[]>>>(
  (acc, context) => ({ ...acc, [context]: [copilotTab] }),
  {},
);

/** Ouvre le panneau sur le côté où l'onglet se trouve (il est déplaçable). */
function openCopilotPanel(): void {
  const layout = useLayoutStore.getState();
  const side = layout.tabPanelAssignments[COPILOT_TAB] ?? 'right';
  if (side === 'left') {
    layout.setLeftDrawerOpen(true);
    layout.setLeftActiveTab(COPILOT_TAB);
  } else {
    layout.setRightDrawerOpen(true);
    layout.setRightActiveTab(COPILOT_TAB);
  }
}

export const adminCopilotManifest: FeatureManifest = {
  id: 'admin-copilot',
  contexts: COPILOT_CONTEXTS,

  panels: { right: { tabs: tabsByContext } },

  enabledWhen: {
    roles: ['admin'],
    featureFlag: COPILOT_FEATURE_FLAG,
  },

  shortcuts: [
    {
      id: 'admin-copilot:open',
      keys: 'mod+i',
      label: 'Ouvrir le copilote',
      scope: 'context',
      contexts: COPILOT_CONTEXTS,
      handler: openCopilotPanel,
    },
  ],
};
